import React, {
  memo, useEffect, useLayoutEffect, useRef,
} from '../lib/teact/teact';
import { getActions, withGlobal } from '../global';

import type { Theme } from '../global/types';
import { AppState } from '../global/types';

import {
  APP_NAME,
  INACTIVE_MARKER,
  IS_ANDROID_DIRECT,
  IS_EXPLORER,
  IS_GRAM_WALLET,
} from '../config';
import { selectCurrentAccountId, selectCurrentAccountSettings, selectCurrentAccountState } from '../global/selectors';
import { useAccentColor } from '../util/accentColor';
import { setActiveTabChangeListener } from '../util/activeTabMonitor';
import buildClassName from '../util/buildClassName';
import { MINUTE } from '../util/dateFormat';
import { closeThisTab } from '../util/ledger/tab';
import { resolveRender } from '../util/renderPromise';
import resolveSlideTransitionName from '../util/resolveSlideTransitionName';
import {
  IS_ELECTRON, IS_LEDGER_EXTENSION_TAB, IS_LINUX,
} from '../util/windowEnvironment';
import { updateSizes } from '../util/windowSize';
import { callApi } from '../api';
import { SwapActivityModal, TransactionInfoModal, TransactionModal } from './main/modals/transaction';
import IFrameBrowser from './ui/IFrameBrowser';

import { useAppIntersectionObserver } from '../hooks/useAppIntersectionObserver';
import useAppTheme from '../hooks/useAppTheme';
import useBackgroundMode from '../hooks/useBackgroundMode';
import { useDeviceScreen } from '../hooks/useDeviceScreen';
import useFlag from '../hooks/useFlag';
import useInterval from '../hooks/useInterval';
import useSyncEffect from '../hooks/useSyncEffect';
import useTimeout from '../hooks/useTimeout';

import Agent from './agent/AgentRuntime';
import AppEmpty from './AppEmpty';
import AppInactive from './AppInactive';
import AppLocked from './appLocked/AppLocked';
import Auth from './auth/Auth';
import AuthImportWalletModal from './auth/AuthImportWalletModal';
import CustomizeWalletModal from './customizeWallet/CustomizeWalletModal';
import DappConnectModal from './dapps/DappConnectModal';
import DappSignDataModal from './dapps/DappSignDataModal';
import DappTransferModal from './dapps/DappTransferModal';
import Dialogs from './Dialogs';
import ElectronHeader from './electron/ElectronHeader';
import Explore from './explore/Explore';
import LedgerModal from './ledger/LedgerModal';
import Main from './main/Main';
import BackupModal from './main/modals/BackupModal';
import NftAttributesModal from './main/modals/NftAttributesModal';
import OffRampWidgetModal from './main/modals/OffRampWidgetModal';
import OnRampWidgetModal from './main/modals/OnRampWidgetModal';
import ReportNftModal from './main/modals/ReportNftModal';
import SignatureModal from './main/modals/SignatureModal';
import UnhideNftModal from './main/modals/UnhideNftModal';
import BottomBar from './main/sections/Actions/BottomBar';
import Toasts from './main/Toasts';
import WalletRenameModal from './main/WalletRenameModal';
import Market from './market/Market';
import MediaViewer from './mediaViewer/MediaViewer';
import MintCardModal from './mintCard/MintCardModal';
import Portfolio from './portfolio/Portfolio';
import Settings from './settings/Settings';
import SwapModal from './swap/SwapModal';
import TokenInfo from './tokenInfo/TokenInfo';
import TransferModal from './transfer/TransferModal';
import ConfettiContainer from './ui/ConfettiContainer';
import LoadingOverlay from './ui/LoadingOverlay';
import Transition from './ui/Transition';
import UpdateAvailable from './ui/UpdateAvailable';
import WalletConnectPayDataCollectionModal from './walletConnectPay/WalletConnectPayDataCollectionModal';
import WalletConnectPayModal from './walletConnectPay/WalletConnectPayModal';
import WalletConnectPayOptionSelectionModal from './walletConnectPay/WalletConnectPayOptionSelectionModal';

// import Test from './components/test/TestNoRedundancy';
import styles from './App.module.scss';

interface StateProps {
  appState: AppState;
  accountId?: string;
  isBackupWalletModalOpen?: boolean;
  isHardwareModalOpen?: boolean;
  isCustomizeWalletModalOpen?: boolean;
  isAgentOpen?: boolean;
  isExploreOpen?: boolean;
  isMarketOpen?: boolean;
  isPortfolioOpen?: boolean;
  currentTokenSlug?: string;
  marketTokenSlug?: string;
  isFullscreen: boolean;
  areSettingsOpen?: boolean;
  theme: Theme;
  accentColorIndex?: number;
  isAppReady?: boolean;
}

const APP_STATES_WITH_BOTTOM_BAR = new Set([
  AppState.Main, AppState.Agent, AppState.Settings, AppState.Explore, AppState.Market, AppState.TokenInfo,
]);
const APP_UPDATE_INTERVAL = (IS_ELECTRON && !IS_LINUX) || IS_ANDROID_DIRECT
  ? 5 * MINUTE
  : undefined;
const PRERENDER_MAIN_DELAY = 1200;
let mainKey = 0;

const APP_STATE_RENDER_COUNT = Object.keys(AppState).length / 2;

function App({
  appState,
  accountId,
  isBackupWalletModalOpen,
  isHardwareModalOpen,
  isCustomizeWalletModalOpen,
  isAgentOpen,
  isExploreOpen,
  isMarketOpen,
  isPortfolioOpen,
  currentTokenSlug,
  marketTokenSlug,
  isFullscreen,
  areSettingsOpen,
  theme,
  accentColorIndex,
  isAppReady,
}: StateProps) {
  const {
    closeBackupWalletModal,
    closeHardwareWalletModal,
    closeSettings,
    cancelCaching,
    checkAppVersion,
  } = getActions();

  const { isPortrait } = useDeviceScreen();
  // Crossing the phone breakpoint swaps the portrait and landscape layouts at once: it is not a move to another screen,
  // and the two layouts would show the same screen twice while one faded into the other
  const isPortraitRef = useRef(isPortrait);
  const isLayoutSwitch = isPortraitRef.current !== isPortrait;
  isPortraitRef.current = isPortrait;

  const [isInactive, markInactive] = useFlag(false);
  const [canPrerenderMain, prerenderMain] = useFlag();

  const renderingKey = resolveRenderingKey({
    isInactive,
    areSettingsOpen,
    isAgentOpen,
    isExploreOpen,
    isMarketOpen,
    isPortfolioOpen,
    currentTokenSlug,
    marketTokenSlug,
    isPortrait,
    appState,
  });
  const withBottomBar = isPortrait && (!IS_EXPLORER || isAppReady) && APP_STATES_WITH_BOTTOM_BAR.has(renderingKey);
  // Screens sharing the bottom bar are sibling tabs, so they cross-fade into each other. The token
  // screen is the exception: the bar stays visible, but the transition slides in.
  const withSlide = isPortrait && (!withBottomBar || renderingKey === AppState.TokenInfo);
  const transitionName = isLayoutSwitch ? 'none' : withSlide ? resolveSlideTransitionName() : 'semiFade';

  useTimeout(
    prerenderMain,
    renderingKey === AppState.Auth && !canPrerenderMain ? PRERENDER_MAIN_DELAY : undefined,
  );

  // Gram Wallet Web is deployed to a domain we do not own and has no store presence, so there is no version to nag about
  useInterval(checkAppVersion, IS_GRAM_WALLET ? undefined : APP_UPDATE_INTERVAL);

  useEffect(() => {
    document.documentElement.classList.toggle('with-bottombar', withBottomBar);
  }, [withBottomBar]);

  useEffect(() => {
    updateSizes();
    setActiveTabChangeListener(() => {
      document.title = `${APP_NAME} ${INACTIVE_MARKER}`;

      markInactive();
      closeSettings();
      cancelCaching();
    });
  }, [markInactive]);

  useBackgroundMode(() => {
    void callApi('setIsAppFocused', false);
  }, () => {
    void callApi('setIsAppFocused', true);
  }, IS_LEDGER_EXTENSION_TAB);

  useLayoutEffect(() => {
    document.documentElement.classList.add('is-rendered');
    resolveRender();
  }, []);
  useLayoutEffect(() => {
    document.documentElement.classList.toggle('is-fullscreen', isFullscreen);
    requestAnimationFrame(updateSizes);
  }, [isFullscreen]);

  useSyncEffect(([previousAccountId]) => {
    // The landscape Agent shares Main, but its conversation belongs to the profile.
    if (accountId && accountId !== previousAccountId && (!isAgentOpen || isPortrait)) {
      mainKey += 1;
    }
  }, [accountId, isAgentOpen, isPortrait]);

  const appTheme = useAppTheme(theme);
  useAccentColor('body', appTheme, accentColorIndex);

  useAppIntersectionObserver();

  function renderContent(isActive: boolean, isFrom: boolean, currentKey: AppState) {
    switch (currentKey) {
      case AppState.Auth:
        return <Auth />;
      case AppState.Main: {
        const slideFullClassName = buildClassName(
          styles.appSlide,
          styles.appSlideContent,
          'custom-scroll',
          'app-slide-content',
        );
        return (
          <Transition
            name="semiFade"
            activeKey={mainKey}
            shouldCleanup
            nextKey={renderingKey === AppState.Auth && canPrerenderMain ? mainKey + 1 : undefined}
            slideClassName={slideFullClassName}
          >
            <Main
              key={mainKey}
              accountId={accountId}
              isActive={isActive}
            />
          </Transition>
        );
      }
      case AppState.Agent:
        return <Agent isActive={isActive} />;
      case AppState.Explore:
        return <Explore isActive={isActive} />;
      case AppState.Market:
        return <Market isActive={isActive} />;
      case AppState.Settings:
        return <Settings isActive={isActive} />;
      case AppState.Portfolio:
        return <Portfolio isActive={isActive} />;
      case AppState.TokenInfo:
        return <TokenInfo isActive={isActive} />;
      case AppState.Ledger:
        return <LedgerModal isOpen noBackdropClose onClose={closeThisTab} />;
      case AppState.Inactive:
        return <AppInactive />;
      case AppState.Empty:
        return <AppEmpty />;
    }
  }

  return (
    <>
      {IS_ELECTRON && <ElectronHeader withTitle />}

      <Transition
        name={transitionName}
        activeKey={renderingKey}
        renderCount={APP_STATE_RENDER_COUNT}
        shouldCleanup={!withBottomBar}
        className={styles.transitionContainer}
        slideClassName={
          buildClassName(styles.appSlide, withBottomBar && styles.appSlide_fastTransition, 'custom-scroll')
        }
      >
        {renderContent}
      </Transition>

      <AppLocked />
      <MediaViewer />
      {!isInactive && (
        <>
          <AuthImportWalletModal />
          <LedgerModal isOpen={isHardwareModalOpen} onClose={closeHardwareWalletModal} />
          <BackupModal
            isOpen={isBackupWalletModalOpen}
            onClose={closeBackupWalletModal}
          />
          <TransferModal />
          <SwapModal />
          <MintCardModal />
          <CustomizeWalletModal isOpen={isCustomizeWalletModalOpen} />
          <SignatureModal />
          <TransactionModal />
          <TransactionInfoModal />
          <SwapActivityModal />
          <DappConnectModal />
          <DappSignDataModal />
          <DappTransferModal />
          <OnRampWidgetModal />
          <OffRampWidgetModal />
          <WalletConnectPayModal />
          <WalletConnectPayOptionSelectionModal />
          <WalletConnectPayDataCollectionModal />
          <UnhideNftModal />
          <ReportNftModal />
          <NftAttributesModal />
          <Toasts />
          <WalletRenameModal />
          <Dialogs />
          <ConfettiContainer />
          <IFrameBrowser />
          <LoadingOverlay />
          {/* Outside `Main`, which portrait screens such as Portfolio and Agent replace. It stays off Auth, whose
          controls sit where the button floats. */}
          {!IS_ELECTRON && renderingKey !== AppState.Auth && <UpdateAvailable />}
        </>
      )}
      {withBottomBar && <BottomBar />}
    </>
  );
}

export default memo(withGlobal((global): StateProps => {
  return {
    appState: global.appState,
    accountId: selectCurrentAccountId(global),
    isBackupWalletModalOpen: global.isBackupWalletModalOpen,
    isHardwareModalOpen: global.isHardwareModalOpen,
    isCustomizeWalletModalOpen: global.isCustomizeWalletModalOpen,
    isAgentOpen: global.isAgentOpen,
    isExploreOpen: global.isExploreOpen,
    isMarketOpen: global.isMarketOpen,
    isPortfolioOpen: global.isPortfolioOpen,
    currentTokenSlug: selectCurrentAccountState(global)?.currentTokenSlug,
    marketTokenSlug: global.marketTokenSlug,
    areSettingsOpen: global.areSettingsOpen,
    isFullscreen: Boolean(global.isFullscreen),
    theme: global.settings.theme,
    accentColorIndex: selectCurrentAccountSettings(global)?.accentColorIndex,
    isAppReady: selectCurrentAccountState(global)?.isAppReady,
  };
})(App));

function resolveRenderingKey({
  isInactive,
  areSettingsOpen,
  isAgentOpen,
  isExploreOpen,
  isMarketOpen,
  isPortfolioOpen,
  currentTokenSlug,
  marketTokenSlug,
  isPortrait,
  appState,
}: {
  isInactive: boolean;
  areSettingsOpen?: boolean;
  isAgentOpen?: boolean;
  isExploreOpen?: boolean;
  isMarketOpen?: boolean;
  isPortfolioOpen?: boolean;
  currentTokenSlug?: string;
  marketTokenSlug?: string;
  isPortrait: boolean;
  appState: AppState;
}) {
  if (isInactive) return AppState.Inactive;
  if (areSettingsOpen && isPortrait) return AppState.Settings;
  if (isAgentOpen && isPortrait) return AppState.Agent;
  if (isExploreOpen && isPortrait) return AppState.Explore;
  // A token opened from the market shows on top of the showcase, so the market tab stays selected.
  // A token opened in the wallet stays behind the market, like behind Explore
  if (isMarketOpen && isPortrait) {
    return currentTokenSlug && currentTokenSlug === marketTokenSlug ? AppState.TokenInfo : AppState.Market;
  }
  if (isPortfolioOpen && isPortrait) return AppState.Portfolio;
  // In landscape the token screen lives inside the main content, next to the wallet overview
  if (currentTokenSlug && isPortrait && appState === AppState.Main) return AppState.TokenInfo;
  return appState;
}
